import { useEffect, useState } from "react";
import { Button, CheckCheck, CircleX, NoticeBox, RefreshCcw, Tags, Undo2 } from "../../ui";
import CopilotOperationCard from "./CopilotOperationCard";
import { PLAN_STATUS_VARIANT } from "./copilotModel";

// A proposed set of changes. Nothing is written until "Apply"; the backend
// executes exactly this plan revision (or refuses and refreshes it when the
// menu changed meanwhile).
export default function CopilotPlanCard({ plan, t, lang, canWrite, busy, onConfirm, onCancel, onEdit, onUndo, onRegenerate, onUploadImage }) {
  // Verification is per operation and per revision: any edit asks again.
  const [verified, setVerified] = useState([]);
  useEffect(() => setVerified([]), [plan._id, plan.revision]);
  const toVerify = plan.operations.filter((op) => op.preview?.requiresVerification).map((op) => op.index);
  const allVerified = toVerify.every((index) => verified.includes(index));
  const expired = plan.status === "expired" || plan.expired;
  const [showApplied, setShowApplied] = useState(false);
  const pending = plan.status === "pending" && !plan.expired;
  const editable = pending && canWrite;
  const count = plan.operations.length;
  const done = ["completed", "partially_completed", "failed"].includes(plan.status);
  const showOperations = !done || showApplied;
  const undone = plan.undo?.status === "undone";

  const exclude = (index, operation) => (rowId) => onEdit([{ index, params: { excludeIds: [...(operation.params?.excludeIds || []), rowId] } }]);

  return (
    <section className={`copilot-plan is-${plan.status}`} aria-label={t.changesPrepared(count)}>
      <header className="copilot-plan-head">
        <strong>{t.changesPrepared(count)}</strong>
        <Tags variant={undone ? "neutral" : PLAN_STATUS_VARIANT[plan.status] || "neutral"} size="sm">
          {undone ? t.undone : t.status[plan.status] || plan.status}
        </Tags>
      </header>

      {pending && plan.staleNotice && (
        <NoticeBox variant="warning" size="sm">
          {t.staleNote}
        </NoticeBox>
      )}

      {showOperations && (
        <div className="copilot-plan-ops">
          {plan.operations.map((operation) => (
            <CopilotOperationCard
              key={`${plan._id}-${operation.index}-${plan.revision}`}
              operation={operation}
              t={t}
              lang={lang}
              editable={editable}
              busy={busy}
              onEdit={({ params, values }) => onEdit([{ index: operation.index, ...(Object.keys(params).length ? { params } : {}), ...(Object.keys(values).length ? { values } : {}) }])}
              onRemove={count > 0 ? () => onEdit([{ index: operation.index, remove: true }]) : null}
              onExclude={exclude(operation.index, operation)}
              verified={verified.includes(operation.index)}
              onVerify={(checked) => setVerified((list) => (checked ? [...new Set([...list, operation.index])] : list.filter((i) => i !== operation.index)))}
              onUploadImage={onUploadImage ? (file) => onUploadImage(operation.index, file) : null}
            />
          ))}
        </div>
      )}

      {plan.status === "completed" && !undone && <NoticeBox variant="success" size="sm">{t.appliedNote(count)}</NoticeBox>}
      {plan.status === "partially_completed" && (
        <NoticeBox variant="warning" size="sm" title={t.partialNote}>
          <ul className="copilot-steps">
            {(plan.execution?.steps || []).map((step, i) => (
              <li key={i}>
                {t.stages[step.stage] || step.stage}
                {step.type ? ` · ${t.ops[step.type] || step.type}` : ""}: {step.status === "done" ? t.stepDone : `${t.stepFailed}${step.error?.message ? ` (${step.error.message})` : ""}`}
              </li>
            ))}
          </ul>
        </NoticeBox>
      )}
      {plan.status === "failed" && (
        <NoticeBox variant={plan.execution?.rollback?.status === "done" ? "warning" : "danger"} size="sm" title={plan.execution?.rollback?.status === "done" ? t.rolledBackNote : t.failedNote}>
          {plan.execution?.error?.message}
        </NoticeBox>
      )}

      {expired && (
        <NoticeBox variant="warning" size="sm">
          {plan.regeneratedTo ? t.regeneratedTo : t.expiredNote}
        </NoticeBox>
      )}

      {pending && plan.blocking && <NoticeBox variant="danger" size="sm">{t.blockedNote}</NoticeBox>}


      <footer className="copilot-plan-actions">
        {pending && canWrite && (
          <>
            <Button type="button" variant="success" appearance="filled" size="sm" rounded icon={<CheckCheck size={15} />} loading={busy} disabled={plan.blocking || !allVerified} onClick={() => onConfirm({ verifiedOperations: verified })}>
              {t.applyAll(count)}
            </Button>
            <Button type="button" variant="warning" appearance="soft" size="sm" rounded icon={<CircleX size={15} />} disabled={busy} onClick={onCancel}>
              {t.cancel}
            </Button>
          </>
        )}
        {expired && canWrite && !plan.regeneratedTo && onRegenerate && (
          <Button type="button" variant="primary" appearance="filled" size="sm" rounded icon={<RefreshCcw size={15} />} loading={busy} onClick={onRegenerate}>
            {t.regenerate}
          </Button>
        )}
        {done && (
          <Button type="button" variant="primary" appearance="text" size="sm" aria-expanded={showApplied} onClick={() => setShowApplied((v) => !v)}>
            {showApplied ? t.hideChanges : t.viewChanges}
          </Button>
        )}
        {plan.status === "completed" && plan.undo?.available && canWrite && (
          <Button type="button" variant="primary" appearance="soft" size="sm" rounded icon={<Undo2 size={15} />} loading={busy} onClick={onUndo}>
            {t.undo}
          </Button>
        )}
        {done && !plan.undo?.available && !undone && plan.status !== "failed" && <small className="menu-secondary">{t.undoUnavailable}</small>}
      </footer>
    </section>
  );
}
